import { ExclamationCircleFilled, PlusOutlined } from "@ant-design/icons";
import type { Dispatch } from "@reduxjs/toolkit";
import { Button, Form, Input, Select, Skeleton, Tag, Typography } from "antd";
import type { AnyObject } from "antd/es/_util/type";
import type { HookAPI } from "antd/es/modal/useModal";
import { connect, type DispatchProp } from "react-redux";
import { actions as routerActions } from "redux-router5";
import type { State as RouterState } from "router5";
import ConnectedAppLayout from "../AppLayout";
import type { RootState } from "../store";
import * as commonColumns from "../../common/columns";
import { Card } from "../../common/components/Card";
import { DrawerItemWrapper } from "../../common/components/DrawerItemWrapper";
import { ListActionBar } from "../../common/components/ListActionBar";
import { PageTitleContainer } from "../../common/components/PageTitle";
import {
	TableWrapper,
	type PaginationAndFilter,
	type TableWrapperProps,
} from "../../common/components/Table";
import { projectFilter } from "../common/filter";
import { sanitizedFilter } from "../../common/functions";
import { routeNames } from "../common/routes";
import { projectActions } from "../../features/project/projectSlice";
import type {
	CurrentUser,
	PaginatedProjectList,
	Project,
} from "../generated-axios-client";
import {
	PROJECT_STATUS_COLORS,
	PROJECT_STATUS_LABELS,
} from "../project/projectConstants";
import { ClientSelectorFormItem } from "../turbine/filter-forms/ClientSelectorFormItem";

const projectStatusOptions = [
	{ value: "ACTIVE", label: PROJECT_STATUS_LABELS.ACTIVE },
	{ value: "COMPLETED", label: PROJECT_STATUS_LABELS.COMPLETED },
	{ value: "DELAYED", label: PROJECT_STATUS_LABELS.DELAYED },
];

const onFilter =
	(
		dispatch: Dispatch,
		form: ReturnType<typeof Form.useForm>[0],
		currentRoute: RouterState,
	) =>
	() => {
		form
			.validateFields()
			.then((values) => {
				dispatch(
					routerActions.navigateTo(
						currentRoute.name,
						sanitizedFilter({ ...currentRoute.params, ...values, page: 1 }),
					),
				);
			})
			.catch(() => {});
	};

const onCancel =
	(
		dispatch: Dispatch,
		form: ReturnType<typeof Form.useForm>[0],
		currentRoute: RouterState,
	) =>
	() => {
		dispatch(
			routerActions.navigateTo(
				currentRoute.name,
				sanitizedFilter({ ...currentRoute.params, ...projectFilter, page: 1 }),
			),
		);
		form.resetFields();
	};

type RowData = {
	user?: CurrentUser;
	results: PaginatedProjectList;
	clientMap: Record<string, string>;
};

const getColumns: TableWrapperProps<
	PaginatedProjectList,
	AnyObject
>["getColumns"] = (intl, currentFilter) => [
	{ dataIndex: "index", key: "index", title: "#" },
	commonColumns.simpleColumn(
		"name",
		"project_table_column_name",
		intl,
		currentFilter,
	),
	commonColumns.simpleColumn(
		"client",
		"project_table_column_client",
		intl,
		currentFilter,
		"client__legal_name",
	),
	commonColumns.simpleColumn(
		"start",
		"project_table_column_start",
		intl,
		currentFilter,
	),
	commonColumns.simpleColumn(
		"end",
		"project_table_column_end",
		intl,
		currentFilter,
	),
	commonColumns.simpleColumn(
		"project_status",
		"project_table_column_status",
		intl,
		currentFilter,
	),
	commonColumns.simpleColumn(
		"created_at",
		"project_table_column_created_at",
		intl,
		currentFilter,
	),
	commonColumns.simpleColumn("actions", "table_column_actions", intl),
];

const getTableRows: TableWrapperProps<RowData, AnyObject>["getRows"] = (
	intl,
	dispatch,
	data,
	modalApi,
) => {
	const { results, user, clientMap } = data;

	return results.results.map((project: Project, index: number) => {
		const showDeleteConfirm = () => {
			(modalApi as HookAPI)?.confirm({
				title: intl.formatMessage({ id: "project_delete_confirmation" }),
				icon: <ExclamationCircleFilled />,
				okText: intl.formatMessage({ id: "confirm" }),
				okType: "danger",
				cancelText: intl.formatMessage({ id: "cancel" }),
				onOk() {
					if (project.id) {
						dispatch(projectActions.deleteProject(project.id));
					}
				},
				onCancel() {},
			});
		};

		const onView = () => {
			dispatch(
				routerActions.navigateTo(routeNames.PROJECT_VIEW, { id: project.id }),
			);
		};

		const onEdit = () => {
			dispatch(
				routerActions.navigateTo(routeNames.PROJECT_EDIT, { id: project.id }),
			);
		};

		const clientName = project.client ? clientMap[project.client] : undefined;

		const onViewClient = () => {
			dispatch(
				routerActions.navigateTo(routeNames.CLIENT_VIEW, { id: project.client }),
			);
		};

		const isOverdue =
			!!project.end &&
			project.project_status !== "COMPLETED" &&
			new Date(project.end) < new Date();

		return {
			key: project.id,
			index: (
				<Typography.Text strong type="secondary">
					{index + 1}
				</Typography.Text>
			),
			name:
				project.id && project.name ? (
					<Typography.Link onClick={onView}>{project.name}</Typography.Link>
				) : (
					(project.name ?? "—")
				),
			project_status:
				project.project_status &&
				project.project_status in PROJECT_STATUS_LABELS ? (
					<Tag
						color={
							project.project_status in PROJECT_STATUS_COLORS
								? PROJECT_STATUS_COLORS[project.project_status]
								: "gray"
						}
						variant="outlined"
						styles={{ root: { fontWeight: 700 } }}
					>
						{PROJECT_STATUS_LABELS[project.project_status]}
					</Tag>
				) : (
					"—"
				),
			start: project.start ? commonColumns.dateValue(project.start, intl) : "—",
			end: project.end ? (
				<Typography.Text type={isOverdue ? "danger" : undefined}>
					{commonColumns.dateValue(project.end, intl)}
				</Typography.Text>
			) : (
				"—"
			),
			client:
				project.client && clientName ? (
					<Typography.Link onClick={onViewClient}>{clientName}</Typography.Link>
				) : (
					"—"
				),
			created_at: project.created_at
				? commonColumns.dateValue(project.created_at, intl)
				: "—",
			actions: commonColumns.actionsValue(
				undefined,
				undefined,
				undefined,
				user,
				onView,
				onEdit,
				showDeleteConfirm,
				undefined,
				"Project",
			),
		};
	});
};

type ProjectsPageProps = ReturnType<typeof withProjects> & DispatchProp;

export const ProjectsPage = (props: ProjectsPageProps) => {
	const { results, dispatch, currentRoute, intl, user, clientMap, clientItems, modalApi } =
		props;

	const [form] = Form.useForm();

	if (!currentRoute || !results) {
		return <Skeleton active />;
	}

	const rowData: RowData = { user, results, clientMap };

	return (
		<ConnectedAppLayout>
			<PageTitleContainer
				title={intl.formatMessage({ id: "project_table_header_name" })}
				subtitle={intl.formatMessage(
					{ id: "project_table_header_number_of_items" },
					{ no: results.pagination.total_items },
				)}
				extra={
					<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={() =>
							dispatch(routerActions.navigateTo(routeNames.PROJECT_CREATE))
						}
					>
						{intl.formatMessage({ id: "project_create_button" })}
					</Button>
				}
			>
				<Card
					title={
						<ListActionBar
							intl={intl}
							form={form}
							onFilter={onFilter(dispatch, form, currentRoute)}
							onCancel={onCancel(dispatch, form, currentRoute)}
							filterTemplate={projectFilter}
							currentFilter={currentRoute.params}
							searchBarPlaceholderKey={intl.formatMessage({
								id: "project_table_header_search_placeholder",
							})}
						>
							<DrawerItemWrapper>
								<Form.Item
									name="project_status"
									label={intl.formatMessage({ id: "filter_label_status" })}
								>
									<Select allowClear options={projectStatusOptions} />
								</Form.Item>
							</DrawerItemWrapper>
							<DrawerItemWrapper>
								<Form.Item
									name="name"
									label={intl.formatMessage({ id: "project_table_column_name" })}
								>
									<Input allowClear />
								</Form.Item>
							</DrawerItemWrapper>
							<DrawerItemWrapper>
								<ClientSelectorFormItem
									intl={intl}
									clientItems={clientItems}
									selectProps={{ allowClear: true }}
								/>
							</DrawerItemWrapper>
						</ListActionBar>
					}
				>
					<TableWrapper
						data={rowData}
						getRows={getTableRows}
						getColumns={getColumns}
						intl={intl}
						modalApi={modalApi}
						route={currentRoute}
						applyFilterAction={(filterParams) =>
							routerActions.navigateTo(routeNames.PROJECT_LIST, filterParams)
						}
						pagination={results.pagination as PaginationAndFilter}
					/>
				</Card>
			</PageTitleContainer>
		</ConnectedAppLayout>
	);
};

const withProjects = (state: RootState) => {
	const clientMap: Record<string, string> = {};
	for (const c of state.project.clients) {
		if (c.id) clientMap[c.id] = c.legal_name;
	}
	return {
		results: state.project.list,
		currentRoute: state.router.route,
		intl: state.intl.datetimeIntl,
		user: state.userstate.user,
		clientMap,
		clientItems: state.project.clients,
		modalApi: state.appFeedback.modal,
	};
};

export const ConnectedProjectsPage = connect(withProjects)(ProjectsPage);
export default ConnectedProjectsPage;
